import type { HeadersFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import { useRouteLoaderData } from '@remix-run/react';

import StaticPage from '~/components/StaticPage';
import { staticPageClientLoader } from '~/components/StaticPage/staticPageClientLoader';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { RootLoaderData } from '~/root';
import { buildHeaders, buildUrl } from '~/services/api/utils';
import { actionSession } from '~/session';
import { extractCustomFieldValues } from '~/utils/extractCustomFieldValues';
import getServerTranslation from '~/utils/getServerTranslation/index.server';
import getStaticPageMetaData from '~/utils/getStaticPageMetaData';
import { metaAttributionFromRequest } from '~/utils/metaAttribution.server';
import { safeFormData } from '~/utils/safeFormData';
import { BASE_CACHE_TAG } from '~/utils/setCacheHeaders/index.server';
import { shouldRevalidateLoaderData } from '~/utils/shouldRevalidateLoaderData';

import type { StaticPageLoaderData } from '../services/static_page';
import { staticPageLoader } from '../services/static_page';

export const action: LoaderFunction = async ({ request }) => {
  try {
    const { session } = await actionSession(request);
    const body = await safeFormData(request);
    const metaAttribution = metaAttributionFromRequest(request);

    const errorMessage = body.get('upgrade_error_message');
    const successMessage = body.get('upgrade_success_message');
    const paymentMethodId = body.get('payment_method');
    const force_three_d_secure = body.get('force_three_d_secure');
    const price_id = body.get('price_id');
    const email = body.get('email');
    const tax_id = body.get('tax_id');
    const tax_id_type = body.get('tax_id_type');
    const premiumOfferId = body.get('premium_offer_id');
    const last_resource_guid = body.get('last_resource_guid');
    const amount_cents = body.get('amount_cents');
    const signupFlowId = body.get('signup_flow_id');
    const visitToken = body.get('visit_token');
    const nectarUpgradeUrl = body.get('nectar_upgrade_url');
    const nectarAnonymousUserId = body.get('nectar_anonymous_user_id');
    const nectarPageBlock = body.get('nectar_page_block');
    const nectarSiteId = body.get('nectar_site_id');
    const is_group = body.get('is_group') === 'true';
    const group_purchase_quantity = body.get('group_purchase_quantity');
    const group_name = body.get('group_name');

    // Gift subscription related fields
    const is_gift = body.get('is_gift') === 'true';
    const message = body.get('message');
    const purchaser_name = body.get('purchaser_name');
    const recipient_email = body.get('recipient_email');
    const start_at = body.get('start_at');

    const isMissingPaymentForIndividual = !is_gift && !is_group && !paymentMethodId;
    const isMissingDataForGroupCheckout = is_group && !paymentMethodId && !group_purchase_quantity;
    const isMissingCoreFields = !price_id || !email;
    const isInvalidSubmission = isMissingPaymentForIndividual || isMissingDataForGroupCheckout || isMissingCoreFields;

    // This should never be the case anyways because data is required on the client side
    // Therefore using a generic message rather than translating across languages a specific error message here is ideal
    if (isInvalidSubmission) {
      return json({
        toast: {
          status: 'error',
          message: errorMessage,
          level: 1,
        },
      });
    }

    const customFieldValues = extractCustomFieldValues(body.entries());
    const endpointHeaders = buildHeaders(request, session);

    if (is_group && !paymentMethodId) {
      const sessionEndpoint = buildUrl('/subscriptions/stripe_session', {
        price_id,
        premium_offer_id: premiumOfferId,
        email,
        last_resource_guid,
        group_purchase_quantity,
        group_name,
        ...metaAttribution,
      });

      return await fetch(sessionEndpoint, {
        headers: endpointHeaders,
      }).then(async (res) => {
        const response = await res.json();

        if (!res.ok) {
          return json({
            toast: {
              status: 'error',
              message: response?.error || errorMessage,
              level: 2,
            },
          });
        }

        if (response?.url) {
          return redirect(response.url);
        }

        return json({
          toast: {
            status: 'error',
            message: errorMessage,
            level: 2,
          },
        });
      });
    }

    const endpoint = buildUrl(`/subscriptions/${is_gift ? 'gift' : 'upgrade'}`);

    return await fetch(endpoint, {
      method: 'PUT',
      headers: endpointHeaders,
      body: JSON.stringify({
        price_id,
        amount_cents,
        email,
        tax_id,
        tax_id_type,
        premium_offer_id: premiumOfferId,
        payment_method_id: paymentMethodId,
        force_three_d_secure,
        last_resource_guid,
        signup_flow_id: signupFlowId,
        visit_token: visitToken,
        nectar_upgrade_url: nectarUpgradeUrl,
        nectar_anonymous_user_id: nectarAnonymousUserId,
        nectar_page_block: nectarPageBlock,
        nectar_site_id: nectarSiteId,
        group_purchase_quantity,
        group_name,
        message,
        purchaser_name,
        recipient_email,
        start_at,
        custom_field_values: customFieldValues,
        ...metaAttribution,
      }),
    }).then(async (res) => {
      const response = await res.json();

      if (!res.ok) {
        const serverResponseError = response?.error;

        return json({
          toast: {
            status: 'error',
            message: serverResponseError || errorMessage,
            level: 2,
          },
        });
      }

      // Occasionally the response will be a 200 but stripe will require an action to further authenticate the user
      const action = response?.upgrade_subscription?.action;
      const requiresAction = action === 'requires_action';
      const requiresConfirmation = action === 'requires_confirmation';
      if (requiresAction || requiresConfirmation) {
        return json({
          action,
          clientSecret: response?.upgrade_subscription?.client_secret,
          redirectToSSOLogin: response?.redirect_to_sso_login ?? false,
          purchaserSubscriberId: response?.purchaser_subscriber_id,
        });
      }

      // When a gift form is submitted, the backend (swarm) creates
      // a stripe session and sends it back as a redirect url
      // if external checkout is enabled
      // Here we redirect the user to Stripe checkout experience
      // instead of redirecting on the client side
      if ((is_gift || is_group) && response?.redirect_url) {
        return redirect(response?.redirect_url);
      }

      return json({
        signupFlow: response?.signup_flow,
        redirectToSSOLogin: response?.redirect_to_sso_login ?? false,
        purchaserSubscriberId: response?.purchaser_subscriber_id,
        toast: {
          status: 'success',
          message: successMessage,
        },
      });
    });
  } catch (_err) {
    const { defaultErrorMessage } = await getServerTranslation(request);

    return json({
      toast: {
        status: 'error',
        message: defaultErrorMessage,
        level: 3,
      },
    });
  }
};

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  return {
    'cache-control': loaderHeaders.get('cache-control') || 'max-age=0', // no-cache
    'Cache-Tag': loaderHeaders.get('Cache-Tag') || BASE_CACHE_TAG,
  };
};

export const meta: MetaFunction<typeof staticPageLoader> = getStaticPageMetaData;
export const loader: LoaderFunction = async ({ request, context }) => {
  return staticPageLoader({
    request,
    context,
    pageRequestParams: {
      pathname: 'upgrade',
    },
  });
};

export const clientLoader = staticPageClientLoader;

const Upgrade = () => {
  const { page } = useTypedLoaderData<StaticPageLoaderData>();
  const { site } = useRouteLoaderData('root') as RootLoaderData;

  return <StaticPage page={page} site={site} />;
};

export const shouldRevalidate = shouldRevalidateLoaderData;

export default Upgrade;
